import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, FlaskConical, Save, Plus, Trash2, Edit3, RefreshCw, Download,
  ShieldAlert, Users, Ticket, Building2, Mail, Radio, BarChart3, ChevronRight,
  ChevronDown, ChevronUp, Send,
} from "lucide-react";
import api, { API_BASE, getSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// ═══════════════════════════════════════════════════════════════════════════
// TEST MODE
// ═══════════════════════════════════════════════════════════════════════════
export function TestModePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [smsTo, setSmsTo] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event/admin/test-mode/status");
      setStatus(data);
      setSmsTo(data.test_sms_recipient || "");
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      await api.put("/event/admin/test-mode", { test_mode: on, test_sms_recipient: smsTo });
      toast.success(on ? "Test Mode enabled." : "Test Mode disabled.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); }
    setBusy(false);
  };

  const saveRecipient = async () => {
    setBusy(true);
    try {
      await api.put("/event/admin/test-mode", { test_mode: status?.test_mode, test_sms_recipient: smsTo });
      toast.success("Test SMS recipient saved.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const action = async (label, fn) => {
    if (!window.confirm(`${label}?`)) return;
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(`${label} · ${JSON.stringify(data)}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || `${label} failed`); }
    setBusy(false);
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  const isOn = !!status?.test_mode;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FlaskConical size={16} className={isOn ? "text-amber-600" : "text-emerald-700"} />
            <h3 className="font-[Cormorant_Garamond] text-xl">Event Testing · Test Mode</h3>
            {isOn && <span data-testid="test-mode-badge" className="text-[9px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">ON</span>}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs font-[Outfit] text-emerald-950/70 leading-relaxed">
            When Test Mode is ON: a banner appears on the volunteer & admin pages, every new
            attendee/check-in is tagged as test data (excluded from live totals), and outbound
            SMS is routed to the Test SMS Recipient instead of the attendee.
          </p>
          <div className="flex items-center gap-3">
            <Button
              data-testid="test-mode-toggle-on"
              onClick={() => toggle(!isOn)}
              disabled={busy}
              className={`rounded-full text-xs h-10 ${isOn ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-950 hover:bg-emerald-900"} text-white`}
            >
              {isOn ? "Turn OFF Test Mode" : "Turn ON Test Mode"}
            </Button>
            <div className="flex-1 grid grid-cols-3 gap-2 text-[10px] font-[Outfit] text-emerald-800/70">
              <MetricPill label="Test attendees" value={status?.counts?.test_attendees ?? 0} />
              <MetricPill label="Test check-ins" value={status?.counts?.test_check_ins ?? 0} />
              <MetricPill label="Test SMS" value={status?.counts?.test_sms ?? 0} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <label>
              <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">
                Test SMS recipient (E.164 or 10-digit US)
              </span>
              <Input
                data-testid="test-mode-sms-recipient"
                value={smsTo}
                onChange={(e) => setSmsTo(e.target.value)}
                placeholder="+17045551234"
                className="mt-1 h-10 font-[Outfit]"
              />
            </label>
            <Button onClick={saveRecipient} disabled={busy} className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-10">
              <Save size={14} className="mr-1.5" /> Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="font-[Cormorant_Garamond] text-xl">Simulate the event</h3>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ActionBtn
            testid="test-mode-generate-attendees"
            label="Generate 14 sample attendees"
            hint="Individual · walk-in · Fri-only · Sat-only · Both · Sponsor · Honoree · Speaker · Panelist · Volunteer · Board · Vendor · Media · Group guest"
            onClick={() => action("Generate 14 sample test attendees", () => api.post("/event/admin/test-mode/generate-attendees"))}
          />
          <ActionBtn
            testid="test-mode-generate-groups"
            label="Generate sample group registrations"
            hint="Claims 2 tickets against Matt Winston"
            onClick={() => action("Generate 2 sample group guests under Matt Winston", () => api.post("/event/admin/test-mode/generate-group-registrations"))}
          />
          <ActionBtn
            testid="test-mode-reset-checkins"
            label="Reset test check-ins"
            hint="Delete every check-in tagged is_test=true, keep attendees so you can re-check them in"
            onClick={() => action("Reset all test check-ins", () => api.post("/event/admin/test-mode/reset-checkins"))}
          />
          <ActionBtn
            testid="test-mode-delete-all"
            label="Delete ALL demo data"
            hint="Nukes every attendee/check-in/claim/SMS tagged as test"
            danger
            onClick={() => action("Delete ALL test/demo data — cannot be undone", () => api.delete("/event/admin/test-mode/all-demo-data"))}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MetricPill({ label, value }) {
  return (
    <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-2 py-1.5">
      <div className="uppercase tracking-widest">{label}</div>
      <div className="text-sm font-bold text-emerald-950">{value}</div>
    </div>
  );
}
function ActionBtn({ label, hint, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border-2 px-3 py-3 hover:border-fuchsia-400 transition-colors ${danger ? "border-red-200 bg-red-50/40" : "border-emerald-100 bg-white"}`}
    >
      <div className="text-sm font-[Outfit] font-bold text-emerald-950">{label}</div>
      <div className="text-[11px] font-[Outfit] text-emerald-800/60 mt-0.5">{hint}</div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOSTS · GROUP TICKET ALLOCATION
// ═══════════════════════════════════════════════════════════════════════════
export function HostsPanel() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/admin/hosts");
      setHosts(data.hosts || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault?.();
    try {
      const body = {
        name: editing.name,
        organization: editing.organization || "",
        email: editing.email || "",
        phone: editing.phone || "",
        purchased: Number(editing.purchased) || 0,
        notes: editing.notes || "",
        payment_status: editing.payment_status || "",
        active: editing.active !== false,
      };
      if (editing.id) await api.patch(`/event/admin/hosts/${editing.id}`, body);
      else await api.post("/event/admin/hosts", body);
      toast.success("Host saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (host) => {
    if (host.claimed > 0) return toast.error("Cannot delete — deactivate instead (tickets already claimed).");
    if (!window.confirm(`Delete host ${host.name}?`)) return;
    try { await api.delete(`/event/admin/hosts/${host.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/reports/hosts.csv`, { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "host_allocation_report.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          data-testid="host-add"
          onClick={() => setEditing({ name: "", organization: "", email: "", phone: "", purchased: 0, notes: "", payment_status: "paid", active: true })}
          className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-9"
        ><Plus size={14} className="mr-1.5" /> New host</Button>
        <Button
          data-testid="host-csv"
          onClick={downloadCsv}
          className="ml-auto rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9"
        ><Download size={14} className="mr-1.5" /> Export CSV</Button>
      </div>

      {editing && (
        <Card data-testid="host-editor">
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">{editing.id ? "Edit" : "New"} host</h3></CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <TxtLabel label="Name *"><Input data-testid="host-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></TxtLabel>
              <TxtLabel label="Organization"><Input value={editing.organization || ""} onChange={(e) => setEditing({ ...editing, organization: e.target.value })} /></TxtLabel>
              <TxtLabel label="Email"><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></TxtLabel>
              <TxtLabel label="Phone"><Input type="tel" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></TxtLabel>
              <TxtLabel label="Tickets Purchased *">
                <Input data-testid="host-purchased" type="number" min="0" value={editing.purchased} onChange={(e) => setEditing({ ...editing, purchased: e.target.value })} />
              </TxtLabel>
              <TxtLabel label="Payment status">
                <select value={editing.payment_status || ""} onChange={(e) => setEditing({ ...editing, payment_status: e.target.value })} className="h-10 w-full rounded-md border-2 border-emerald-100 px-3">
                  <option value="">—</option>
                  <option value="paid">Paid</option>
                  <option value="complimentary">Complimentary</option>
                  <option value="invoice">Invoice pending</option>
                </select>
              </TxtLabel>
              <TxtLabel label="Notes" className="col-span-full">
                <Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} />
              </TxtLabel>
              <label className="col-span-full text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 rounded" /> Active
              </label>
              <div className="col-span-full flex justify-end gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="host-save" className="bg-emerald-950 hover:bg-emerald-900 text-white"><Save size={14} className="mr-1.5" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          ) : hosts.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No hosts yet.</div>
          ) : (
            <table className="w-full text-xs font-[Outfit]">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr>
                  <th className="text-left px-3 py-2">Host</th>
                  <th className="text-left px-3 py-2">Organization</th>
                  <th className="text-right px-3 py-2">Purchased</th>
                  <th className="text-right px-3 py-2">Claimed</th>
                  <th className="text-right px-3 py-2">Checked In</th>
                  <th className="text-right px-3 py-2">Remaining</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {hosts.map((h) => (
                  <tr key={h.id} data-testid={`host-row-${h.id}`} className="border-t border-emerald-100">
                    <td className="px-3 py-2 font-semibold text-emerald-950">{h.name}</td>
                    <td className="px-3 py-2 text-emerald-900">{h.organization}</td>
                    <td className="px-3 py-2 text-right font-bold">{h.purchased}</td>
                    <td className="px-3 py-2 text-right">{h.claimed}</td>
                    <td className="px-3 py-2 text-right">{h.checked_in}</td>
                    <td className={`px-3 py-2 text-right font-bold ${h.remaining === 0 ? "text-amber-700" : "text-emerald-800"}`}>{h.remaining}</td>
                    <td className="px-3 py-2 text-emerald-900">{h.active ? h.payment_status || "—" : "Inactive"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEditing(h)} className="p-1.5 rounded hover:bg-emerald-50" title="Edit"><Edit3 size={13} /></button>
                      <button onClick={() => del(h)} className="p-1.5 rounded hover:bg-fuchsia-50 text-fuchsia-700" title="Delete"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SITE MODE
// ═══════════════════════════════════════════════════════════════════════════
export function SiteModePanel() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event/admin/site-mode");
      setState(data);
      setDirty({});
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/event/admin/site-mode", dirty);
      toast.success("Site mode updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  const setOverride = (v) => setDirty((d) => ({ ...d, mode_override: v }));
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-fuchsia-700" />
          <h3 className="font-[Cormorant_Garamond] text-xl">Site Display Mode</h3>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl bg-emerald-950 text-white p-4">
          <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">Currently showing</div>
          <div className="font-[Cormorant_Garamond] text-3xl leading-tight capitalize" data-testid="site-mode-current">
            {(state.current?.mode || "").replace("_", " ")} Mode
          </div>
          <div className="text-[11px] font-[Outfit] text-white/60 mt-1">
            Source: {state.current?.source}{" · "}Auto: {state.auto_enabled ? "on" : "off"}{" · "}Override: {state.override || "—"}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["pitch", "event", "post_event"].map((m) => (
            <button
              key={m}
              data-testid={`site-mode-preview-${m}`}
              onClick={() => setOverride(m)}
              className={`rounded-xl border-2 px-3 py-3 text-left ${dirty.mode_override === m ? "border-fuchsia-500 bg-fuchsia-50" : "border-emerald-100 bg-white hover:border-emerald-300"}`}
            >
              <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Preview / Override</div>
              <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 capitalize">{m.replace("_", "-")}</div>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <TxtLabel label="Pitch → Event transition (ISO ET)">
            <Input data-testid="site-mode-pitch-end" defaultValue={state.pitch_ends} onChange={(e) => setDirty((d) => ({ ...d, mode_pitch_end: e.target.value }))} />
          </TxtLabel>
          <TxtLabel label="Event → Post-Event transition (ISO ET)">
            <Input data-testid="site-mode-event-end" defaultValue={state.event_ends} onChange={(e) => setDirty((d) => ({ ...d, mode_event_end: e.target.value }))} />
          </TxtLabel>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
            <input
              data-testid="site-mode-auto"
              type="checkbox"
              defaultChecked={state.auto_enabled}
              onChange={(e) => setDirty((d) => ({ ...d, mode_auto_enabled: e.target.checked }))}
              className="h-4 w-4 rounded"
            /> Auto transitions enabled
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => setDirty((d) => ({ ...d, mode_override: "" }))} data-testid="site-mode-clear-override">Clear override</Button>
            <Button data-testid="site-mode-save" onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
              <Save size={14} className="mr-1.5" /> Save mode
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FOLLOW-UP DRIP
// ═══════════════════════════════════════════════════════════════════════════
export function FollowupPanel() {
  const [state, setState] = useState(null);
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([
        api.get("/event/admin/followups"),
        api.get("/event/admin/followups/audience-count").catch(() => ({ data: {} })),
      ]);
      setState(f.data);
      setAudience(a.data);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (idx, k, v) => {
    const msgs = [...(state.messages || [])];
    msgs[idx] = { ...msgs[idx], [k]: v };
    setState({ ...state, messages: msgs });
  };
  const save = async () => {
    try {
      await api.put("/event/admin/followups", { messages: state.messages });
      // Save survey_url separately via /admin/settings
      await api.put("/event/admin/settings", { survey_url: state.survey_url, survey_headline: state.survey_headline });
      toast.success("Follow-ups saved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const sendTest = async (id) => {
    if (!testTo) return toast.error("Enter a test email above first.");
    try {
      await api.post("/event/admin/followups/test", { message_id: id, to: testTo });
      toast.success("Test email fired · check your inbox in a moment.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Test failed"); }
  };
  const runNow = async () => {
    try {
      const { data } = await api.post("/event/admin/followups/run");
      toast.success(`Worker fired · sent ${data.sent || 0} · skipped ${data.skipped || 0}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Run failed"); }
  };
  if (loading || !state) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><div className="flex items-center gap-2"><Mail size={16} className="text-fuchsia-700" /><h3 className="font-[Cormorant_Garamond] text-xl">Post-Event Survey & Follow-Up Drip</h3></div></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <TxtLabel label="Survey URL"><Input data-testid="survey-url" value={state.survey_url || ""} onChange={(e) => setState({ ...state, survey_url: e.target.value })} placeholder="https://forms.gle/..." /></TxtLabel>
            <TxtLabel label="Survey headline"><Input value={state.survey_headline || ""} onChange={(e) => setState({ ...state, survey_headline: e.target.value })} /></TxtLabel>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
            <TxtLabel label="Send-test-to email (any inbox)"><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="admin@example.com" data-testid="followup-test-to" /></TxtLabel>
            <Button variant="outline" onClick={runNow} data-testid="followup-run-now"><RefreshCw size={14} className="mr-1.5" /> Run worker now</Button>
            <Button onClick={save} data-testid="followup-save" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"><Save size={14} className="mr-1.5" /> Save</Button>
          </div>
          <div className="text-xs font-[Outfit] text-emerald-800/70 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
            <strong>Audience count:</strong> {audience?.count ?? "—"} checked-in attendees with email consent
            {audience?.opted_out != null && <> · {audience.opted_out} opted out</>}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {(state.messages || []).map((m, i) => (
          <Card key={m.id} data-testid={`followup-row-${m.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
                  <input type="checkbox" checked={!!m.active} onChange={(e) => patch(i, "active", e.target.checked)} className="h-4 w-4 rounded" data-testid={`followup-active-${m.id}`} /> Active
                </label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700">{m.channel || "email"}</span>
                <span className="ml-auto text-[11px] font-[Outfit] text-emerald-800/60">
                  Sent: <strong className="text-emerald-950">{m.sent_count || 0}</strong> · Failed: {m.failed_count || 0}
                </span>
                <Button variant="outline" size="sm" onClick={() => sendTest(m.id)} data-testid={`followup-test-${m.id}`}>Send test</Button>
              </div>
              <TxtLabel label="Subject"><Input value={m.subject || ""} onChange={(e) => patch(i, "subject", e.target.value)} /></TxtLabel>
              <TxtLabel label="Body (supports {survey_url}, {photo_upload_url}, {post_event_hub_url})">
                <Textarea rows={3} value={m.body || ""} onChange={(e) => patch(i, "body", e.target.value)} />
              </TxtLabel>
              <TxtLabel label="Send at (ISO UTC, e.g. 2026-07-19T14:00:00Z)"><Input value={m.send_at || ""} onChange={(e) => patch(i, "send_at", e.target.value)} /></TxtLabel>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PROFILES (SPONSOR / VIP / PANELIST)
// ═══════════════════════════════════════════════════════════════════════════
const PROFILE_KINDS = ["sponsor", "partner", "panelist", "speaker", "trailblazer"];

export function ProfilesPanel() {
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/admin/profiles");
      setProfiles(data.profiles || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault?.();
    const body = { ...editing };
    delete body.stats;
    delete body.id;
    try {
      if (editing.id) await api.patch(`/event/admin/profiles/${editing.id}`, body);
      else await api.post("/event/admin/profiles", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try { await api.delete(`/event/admin/profiles/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const downloadCsv = async (slug) => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/profiles/${slug}/analytics.csv`, { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `profile_${slug}_analytics.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="profile-add" onClick={() => setEditing({ kind: "sponsor", name: "", organization: "", sponsor_level: "", bio: "", website: "", cta_label: "", cta_url: "", order: 0, active: true })} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-9"><Plus size={14} className="mr-1.5" /> New profile</Button>
      </div>

      {editing && (
        <Card data-testid="profile-editor">
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">{editing.id ? "Edit" : "New"} profile</h3></CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <TxtLabel label="Kind *">
                <select data-testid="profile-kind" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="h-10 w-full rounded-md border-2 border-emerald-100 px-3">
                  {PROFILE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </TxtLabel>
              <TxtLabel label="Name *"><Input data-testid="profile-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></TxtLabel>
              <TxtLabel label="Organization"><Input value={editing.organization || ""} onChange={(e) => setEditing({ ...editing, organization: e.target.value })} /></TxtLabel>
              <TxtLabel label="Sponsor level"><Input value={editing.sponsor_level || ""} onChange={(e) => setEditing({ ...editing, sponsor_level: e.target.value })} placeholder="Silver · Gold · Legacy…" /></TxtLabel>
              <TxtLabel label="Bio" className="col-span-full"><Textarea rows={3} value={editing.bio || ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} /></TxtLabel>
              <TxtLabel label="Logo / headshot URL"><Input value={editing.logo_url || ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Website"><Input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></TxtLabel>
              <TxtLabel label="LinkedIn"><Input value={editing.linkedin || ""} onChange={(e) => setEditing({ ...editing, linkedin: e.target.value })} /></TxtLabel>
              <TxtLabel label="Facebook"><Input value={editing.facebook || ""} onChange={(e) => setEditing({ ...editing, facebook: e.target.value })} /></TxtLabel>
              <TxtLabel label="Instagram"><Input value={editing.instagram || ""} onChange={(e) => setEditing({ ...editing, instagram: e.target.value })} /></TxtLabel>
              <TxtLabel label="Contact email"><Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></TxtLabel>
              <TxtLabel label="Contact form URL"><Input value={editing.contact_form_url || ""} onChange={(e) => setEditing({ ...editing, contact_form_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Special offer text"><Input value={editing.special_offer || ""} onChange={(e) => setEditing({ ...editing, special_offer: e.target.value })} /></TxtLabel>
              <TxtLabel label="Special offer URL"><Input value={editing.special_offer_url || ""} onChange={(e) => setEditing({ ...editing, special_offer_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Resource URL"><Input value={editing.resource_url || ""} onChange={(e) => setEditing({ ...editing, resource_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Resource label"><Input value={editing.resource_label || ""} onChange={(e) => setEditing({ ...editing, resource_label: e.target.value })} /></TxtLabel>
              <TxtLabel label="Schedule URL"><Input value={editing.schedule_url || ""} onChange={(e) => setEditing({ ...editing, schedule_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Primary CTA label"><Input value={editing.cta_label || ""} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} /></TxtLabel>
              <TxtLabel label="Primary CTA URL"><Input value={editing.cta_url || ""} onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })} /></TxtLabel>
              <TxtLabel label="Order"><Input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} /></TxtLabel>
              <label className="col-span-full text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 rounded" /> Active
              </label>
              <div className="col-span-full flex justify-end gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="profile-save" className="bg-emerald-950 hover:bg-emerald-900 text-white"><Save size={14} className="mr-1.5" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          ) : profiles.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No profiles yet.</div>
          ) : (
            <table className="w-full text-xs font-[Outfit]">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Kind</th><th className="text-right px-3 py-2">Views</th><th className="text-right px-3 py-2">Clicks</th><th className="text-right px-3 py-2">Unique</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {profiles.map((p) => {
                  const s = p.stats || {};
                  const clicks = ["website_clicks", "linkedin_clicks", "facebook_clicks", "instagram_clicks", "resource_clicks", "offer_clicks", "schedule_clicks", "contact_clicks", "cta_clicks"].reduce((n, k) => n + (s[k] || 0), 0);
                  return (
                    <tr key={p.id} data-testid={`profile-row-${p.id}`} className="border-t border-emerald-100">
                      <td className="px-3 py-2 font-semibold text-emerald-950">{p.name}</td>
                      <td className="px-3 py-2"><span className="uppercase tracking-widest text-[9px] font-bold bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded-full">{p.kind}</span></td>
                      <td className="px-3 py-2 text-right">{s.views || 0}</td>
                      <td className="px-3 py-2 text-right">{clicks}</td>
                      <td className="px-3 py-2 text-right">{s.unique_visitors || 0}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => downloadCsv(p.slug)} className="p-1.5 rounded hover:bg-emerald-50" title="Analytics CSV"><Download size={13} /></button>
                        <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-emerald-50" title="Edit"><Edit3 size={13} /></button>
                        <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-fuchsia-50 text-fuchsia-700" title="Delete"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REPORTS SUMMARY (Phase G)
// ═══════════════════════════════════════════════════════════════════════════
export function ReportsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event/admin/reports/summary");
      setData(data);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  const a = data.attendance || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Metric label="Registered" value={a.total_registered} />
        <Metric label="Checked In" value={a.total_checked_in} />
        <Metric label="Friday" value={a.friday} />
        <Metric label="Saturday" value={a.saturday} />
        <Metric label="Both Days" value={a.both_days} />
        <Metric label="Walk-Ins" value={a.walk_ins} />
        <Metric label="Check-Outs" value={a.check_outs} />
        <Metric label="Reversed" value={a.reversed} />
      </div>
      <Card>
        <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">Host Allocation</h3></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs font-[Outfit]">
            <thead className="bg-emerald-50 text-emerald-900">
              <tr><th className="text-left px-3 py-2">Host</th><th className="text-right px-3 py-2">Purchased</th><th className="text-right px-3 py-2">Claimed</th><th className="text-right px-3 py-2">Checked In</th><th className="text-right px-3 py-2">Remaining</th></tr>
            </thead>
            <tbody>
              {(data.hosts?.list || []).map((h) => (
                <tr key={h.id} className="border-t border-emerald-100">
                  <td className="px-3 py-2 font-semibold text-emerald-950">{h.name}</td>
                  <td className="px-3 py-2 text-right font-bold">{h.purchased}</td>
                  <td className="px-3 py-2 text-right">{h.claimed}</td>
                  <td className="px-3 py-2 text-right">{h.checked_in}</td>
                  <td className={`px-3 py-2 text-right font-bold ${h.remaining === 0 ? "text-amber-700" : "text-emerald-800"}`}>{h.remaining}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-emerald-950 bg-emerald-50/60">
                <td className="px-3 py-2 font-bold text-emerald-950">Total</td>
                <td className="px-3 py-2 text-right font-bold">{data.hosts?.totals?.purchased || 0}</td>
                <td className="px-3 py-2 text-right font-bold">{data.hosts?.totals?.claimed || 0}</td>
                <td className="px-3 py-2 text-right"></td>
                <td className="px-3 py-2 text-right font-bold">{data.hosts?.totals?.remaining || 0}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">Attendance by Category</h3></CardHeader>
          <CardContent>
            <ul className="text-sm font-[Outfit] space-y-1">
              {Object.entries(data.by_category || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between"><span className="text-emerald-950">{k}</span><span className="font-bold">{v}</span></li>
              ))}
              {Object.keys(data.by_category || {}).length === 0 && <li className="text-emerald-800/60">No data yet.</li>}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">Communications</h3></CardHeader>
          <CardContent>
            <ul className="text-sm font-[Outfit] space-y-1">
              <li className="flex justify-between"><span>SMS sent (live)</span><span className="font-bold">{data.communications?.sms_sent_live || 0}</span></li>
              <li className="flex justify-between"><span>SMS sent (test-routed)</span><span className="font-bold">{data.communications?.sms_sent_test || 0}</span></li>
              <li className="flex justify-between"><span>Follow-up emails sent</span><span className="font-bold">{data.communications?.followup_sent || 0}</span></li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-3">
      <div className="text-[10px] font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60">{label}</div>
      <div className="mt-1 font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950">{value ?? 0}</div>
    </div>
  );
}

function TxtLabel({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VOLUNTEER SUBMISSIONS (Section 6 · general Freedom In Me volunteer form)
// ═══════════════════════════════════════════════════════════════════════════
const VOL_STATUSES = ["new", "contacted", "confirmed", "declined", "future_opportunity"];

export function VolunteersPanel() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", interest: "", q: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/admin/volunteers", { params: filter });
      setRows(data.volunteers || []);
      setCounts(data.counts || {});
    } catch { /* noop */ }
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (row, status) => {
    try {
      await api.patch(`/event/admin/volunteers/${row.id}`, { status });
      toast.success(`Marked ${row.first_name} · ${status.replace("_", " ")}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const saveDetails = async () => {
    if (!selected) return;
    try {
      await api.patch(`/event/admin/volunteers/${selected.id}`, {
        notes: selected.notes || "",
        assigned_to: selected.assigned_to || "",
        status: selected.status,
      });
      toast.success("Saved");
      setSelected(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/volunteers/export.csv`, { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "volunteer_submissions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><div className="flex items-center gap-2"><Users size={16} className="text-fuchsia-700" /><h3 className="font-[Cormorant_Garamond] text-xl">Volunteer Submissions</h3></div></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px] font-[Outfit] uppercase tracking-widest">
            {VOL_STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`vol-count-${s}`}
                onClick={() => setFilter((f) => ({ ...f, status: f.status === s ? "" : s }))}
                className={`rounded-lg border-2 px-2 py-2 text-left ${filter.status === s ? "border-fuchsia-500 bg-fuchsia-50" : "border-emerald-100 bg-white"}`}
              >
                <div>{s.replace("_", " ")}</div>
                <div className="text-lg font-bold text-emerald-950 lowercase">{counts[s] || 0}</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <Input data-testid="vol-search" placeholder="Search name / email / org…" value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} />
            <Button onClick={downloadCsv} data-testid="vol-csv" className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-10"><Download size={14} className="mr-1.5" /> Export CSV</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No volunteer submissions yet.</div>
          ) : (
            <table className="w-full text-xs font-[Outfit]">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr>
                  <th className="text-left px-3 py-2">Submitted</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Interests</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`vol-row-${r.id}`} className="border-t border-emerald-100">
                    <td className="px-3 py-2 whitespace-nowrap">{(r.created_at || "").split("T")[0]}</td>
                    <td className="px-3 py-2 font-semibold text-emerald-950">{r.first_name} {r.last_name}<div className="text-[10px] text-emerald-800/60">{r.phone}</div></td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{r.email}</td>
                    <td className="px-3 py-2">{(r.interests || []).slice(0, 3).join(", ")}{(r.interests || []).length > 3 && " …"}</td>
                    <td className="px-3 py-2">
                      <select value={r.status || "new"} onChange={(e) => updateStatus(r, e.target.value)} className="h-8 rounded border border-emerald-200 px-1 text-xs font-[Outfit]">
                        {VOL_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right"><button onClick={() => setSelected({ ...r })} className="p-1.5 rounded hover:bg-emerald-50" title="Open"><Edit3 size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card data-testid="vol-detail">
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">{selected.first_name} {selected.last_name}</h3></CardHeader>
          <CardContent className="space-y-2 text-sm font-[Outfit]">
            <div className="grid grid-cols-2 gap-2">
              <div><strong>Email:</strong> {selected.email}</div>
              <div><strong>Phone:</strong> {selected.phone}</div>
              <div><strong>City/State:</strong> {selected.city} {selected.state}</div>
              <div><strong>Organization:</strong> {selected.organization || "—"}</div>
              <div><strong>Availability:</strong> {selected.availability || "—"}</div>
              <div><strong>Preferred contact:</strong> {selected.preferred_contact || "—"}</div>
            </div>
            <div><strong>Interests:</strong> {(selected.interests || []).join(", ")}</div>
            {(selected.fim_5k_details || []).length > 0 && <div><strong>5K details:</strong> {selected.fim_5k_details.join(", ")}</div>}
            {selected.skills && <div><strong>Skills:</strong> {selected.skills}</div>}
            {selected.reason && <div><strong>Why:</strong> {selected.reason}</div>}
            <TxtLabel label="Assigned staff"><Input value={selected.assigned_to || ""} onChange={(e) => setSelected({ ...selected, assigned_to: e.target.value })} /></TxtLabel>
            <TxtLabel label="Internal notes"><Textarea rows={3} value={selected.notes || ""} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} /></TxtLabel>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
              <Button onClick={saveDetails} data-testid="vol-detail-save" className="bg-emerald-950 hover:bg-emerald-900 text-white"><Save size={14} className="mr-1.5" /> Save</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// 2027 EARLY ACCESS SUBMISSIONS — displays entries from event_early_access.
// Previously the only way to see this data was via the CSV download button,
// so submissions felt "invisible" to admins. This panel lists them inline
// with a filter + count.
// ═══════════════════════════════════════════════════════════════════════════
export function EarlyAccessPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [interest, setInterest] = useState("");
  const [selected, setSelected] = useState(null);
  // Digest control state
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestConfig, setDigestConfig] = useState({ enabled: true, recipient: "", hour: 8 });
  const [digestHistory, setDigestHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/admin/early-access");
      setRows(data.leads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load 2027 Early Access entries");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load digest config from settings + recent history when the accordion opens
  useEffect(() => {
    if (!digestOpen) return;
    (async () => {
      try {
        const { data: cfg } = await api.get("/event/admin/settings");
        setDigestConfig({
          enabled: cfg?.settings?.digest_enabled ?? true,
          recipient: cfg?.settings?.digest_recipient || "info@itsthefreedominme.com",
          hour: cfg?.settings?.digest_hour ?? 8,
        });
      } catch { /* noop */ }
      try {
        const { data: h } = await api.get("/event/admin/digest/history?limit=5");
        setDigestHistory(h.digests || []);
      } catch { /* noop */ }
    })();
  }, [digestOpen]);

  const saveDigestConfig = async () => {
    setDigestBusy(true);
    try {
      await api.put("/event/admin/settings", {
        digest_enabled: digestConfig.enabled,
        digest_recipient: digestConfig.recipient.trim(),
        digest_hour: Number(digestConfig.hour),
      });
      toast.success("Digest settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save digest settings");
    } finally { setDigestBusy(false); }
  };

  const sendDigestNow = async () => {
    if (!window.confirm(`Send digest email to ${digestConfig.recipient} now?\n\nSummarises yesterday's 2027 Early Access signups.`)) return;
    setDigestBusy(true);
    try {
      const { data } = await api.post("/event/admin/digest/send-now");
      if (data.status === "sent") {
        toast.success(`Digest sent · ${data.count} signup${data.count === 1 ? "" : "s"} summarised`);
      } else {
        toast.warning(`Not sent · ${data.status}`);
      }
      const { data: h } = await api.get("/event/admin/digest/history?limit=5");
      setDigestHistory(h.digests || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Digest send failed");
    } finally { setDigestBusy(false); }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/early-access.csv`, {
        headers: { Authorization: `Bearer ${getSessionToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `2027_early_access_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  // Extract all unique interest types for the filter dropdown
  const interestOptions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => (r.interest_types || []).forEach((i) => s.add(i)));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (interest && !(r.interest_types || []).includes(interest)) return false;
    if (q) {
      const ql = q.toLowerCase();
      const hay = `${r.first_name} ${r.last_name} ${r.email} ${r.phone} ${r.organization}`.toLowerCase();
      if (!hay.includes(ql)) return false;
    }
    return true;
  }), [rows, q, interest]);

  return (
    <div className="space-y-4">
      <Card data-testid="early-access-panel">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 size={16} className="text-fuchsia-700" />
              <h3 className="font-[Cormorant_Garamond] text-xl">2027 Early Access</h3>
              <span
                data-testid="early-access-count"
                className="text-[10px] font-[Outfit] font-bold uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 rounded-full px-2 py-0.5"
              >
                {rows.length} {rows.length === 1 ? "entry" : "entries"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="early-access-refresh">
                <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="early-access-csv">
                <Download size={12} className="mr-1" /> CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <Input
              placeholder="Search name, email, phone, org…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="early-access-search"
            />
            <select
              className="h-10 rounded-md border border-emerald-200 bg-white px-2 text-sm font-[Outfit]"
              value={interest}
              onChange={(e) => setInterest(e.target.value)}
              data-testid="early-access-interest"
            >
              <option value="">All interests</option>
              {interestOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="animate-spin inline-block text-emerald-700" size={20} /></div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm font-[Outfit] text-emerald-950/60">
              {rows.length === 0
                ? "No 2027 Early Access submissions yet. When guests submit the form at /event/early-access, they'll appear here."
                : "No entries match your filters."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-emerald-950/60 font-[Outfit] font-bold uppercase tracking-wider">
                  <tr className="border-b border-emerald-100">
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Interests</th>
                    <th className="px-3 py-2 text-left">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`early-access-row-${r.id}`}
                      className="border-b border-emerald-50 hover:bg-fuchsia-50/40 cursor-pointer"
                      onClick={() => setSelected(r)}
                    >
                      <td className="px-3 py-2 font-[Outfit] font-semibold text-emerald-950">
                        {`${r.first_name || ""} ${r.last_name || ""}`.trim() || "—"}
                      </td>
                      <td className="px-3 py-2 text-emerald-950/80">{r.email}</td>
                      <td className="px-3 py-2 text-emerald-950/70">{r.phone || "—"}</td>
                      <td className="px-3 py-2 text-emerald-950/70">
                        {(r.interest_types || []).slice(0, 3).join(", ")}
                        {(r.interest_types || []).length > 3 && " …"}
                      </td>
                      <td className="px-3 py-2 text-emerald-950/60 whitespace-nowrap">
                        {new Date(r.submitted_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card data-testid="early-access-detail">
          <CardHeader>
            <div className="flex justify-between">
              <h3 className="font-[Cormorant_Garamond] text-xl">
                {`${selected.first_name || ""} ${selected.last_name || ""}`.trim()}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm font-[Outfit] text-emerald-950">
            <div><strong>Email:</strong> {selected.email}</div>
            <div><strong>Phone:</strong> {selected.phone || "—"}</div>
            <div><strong>Organization:</strong> {selected.organization || "—"}</div>
            <div><strong>City / State:</strong> {[selected.city, selected.state].filter(Boolean).join(", ") || "—"}</div>
            <div><strong>Interests:</strong> {(selected.interest_types || []).join(", ") || "—"}</div>
            <div><strong>SMS consent:</strong> {selected.consent_sms ? "Yes" : "No"} · <strong>Email consent:</strong> {selected.consent_email ? "Yes" : "No"}</div>
            <div><strong>Referral / Campaign:</strong> {selected.referral_source || "—"} / {selected.campaign_source || "—"}</div>
            <div><strong>Submitted:</strong> {new Date(selected.submitted_at).toLocaleString()}</div>
          </CardContent>
        </Card>
      )}

      {/* Daily Digest control · admins can toggle the auto-email, change the
          recipient/hour, and fire a one-off "send now" for testing. */}
      <Card data-testid="digest-panel">
        <CardHeader>
          <button
            onClick={() => setDigestOpen((v) => !v)}
            data-testid="digest-toggle"
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-fuchsia-700" />
              <h3 className="font-[Cormorant_Garamond] text-xl">Daily Digest Email</h3>
              <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold bg-fuchsia-100 text-fuchsia-800 px-2 py-0.5 rounded-full">
                {digestConfig.enabled ? "on" : "off"}
              </span>
            </div>
            {digestOpen ? <ChevronUp size={16} className="text-emerald-700" /> : <ChevronDown size={16} className="text-emerald-700" />}
          </button>
        </CardHeader>
        {digestOpen && (
          <CardContent className="space-y-4">
            <div className="rounded-xl border-2 border-emerald-100 bg-emerald-50/40 p-3 text-xs font-[Outfit] text-emerald-900">
              A summary email of yesterday&rsquo;s new 2027 Early Access signups fires daily at the configured hour (Eastern time). If no one signed up, a &ldquo;no new signups&rdquo; email is still sent so you know the pipeline is healthy. Idempotent — only fires once per calendar day.
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="flex flex-col text-xs font-[Outfit] font-semibold text-emerald-950/70">
                <span className="mb-1">Enabled</span>
                <select
                  className="h-10 rounded-md border border-emerald-200 bg-white px-2 text-sm"
                  value={digestConfig.enabled ? "1" : "0"}
                  onChange={(e) => setDigestConfig((c) => ({ ...c, enabled: e.target.value === "1" }))}
                  data-testid="digest-enabled"
                >
                  <option value="1">Yes — send daily</option>
                  <option value="0">No — paused</option>
                </select>
              </label>
              <label className="flex flex-col text-xs font-[Outfit] font-semibold text-emerald-950/70">
                <span className="mb-1">Recipient</span>
                <Input
                  type="email"
                  value={digestConfig.recipient}
                  onChange={(e) => setDigestConfig((c) => ({ ...c, recipient: e.target.value }))}
                  data-testid="digest-recipient"
                />
              </label>
              <label className="flex flex-col text-xs font-[Outfit] font-semibold text-emerald-950/70">
                <span className="mb-1">Send hour (ET)</span>
                <select
                  className="h-10 rounded-md border border-emerald-200 bg-white px-2 text-sm"
                  value={digestConfig.hour}
                  onChange={(e) => setDigestConfig((c) => ({ ...c, hour: Number(e.target.value) }))}
                  data-testid="digest-hour"
                >
                  {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                    <option key={h} value={h}>
                      {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={saveDigestConfig} disabled={digestBusy} data-testid="digest-save" className="rounded-full h-10 bg-emerald-950 hover:bg-emerald-900 text-white">
                {digestBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                Save settings
              </Button>
              <Button onClick={sendDigestNow} disabled={digestBusy || !digestConfig.recipient.trim()} data-testid="digest-send-now" variant="outline" className="rounded-full h-10">
                <Send size={14} className="mr-1.5" /> Send digest now
              </Button>
            </div>

            {digestHistory.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-950/60 mb-1">Recent digests</div>
                <ul className="space-y-1 text-xs font-[Outfit]">
                  {digestHistory.map((h) => (
                    <li key={h.id} className="flex items-center justify-between py-1 border-b border-emerald-50" data-testid={`digest-history-${h.digest_date}`}>
                      <span className="text-emerald-950">
                        <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase mr-2 ${h.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{h.status}</span>
                        {h.digest_date} · {h.count} signup{h.count === 1 ? "" : "s"}
                      </span>
                      <span className="text-emerald-950/50">→ {h.recipient}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
